/**
 * Centralized Add to Cart logic with Modal Support
 */

// Rate limiting: prevent multiple simultaneous requests
let isAddingToCart = false;
let lastAddToCartTime = 0;
const MIN_TIME_BETWEEN_REQUESTS = 500; // 500ms minimum between requests

/**
 * @param {HTMLElement} button
 */
async function addToCartWithModal(button) {
    // Rate limiting: prevent multiple simultaneous requests
    const now = Date.now();
    if (isAddingToCart) {
        console.warn('Already adding to cart, please wait.');
        return;
    }

    // Debounce: prevent rapid clicks
    if (now - lastAddToCartTime < MIN_TIME_BETWEEN_REQUESTS) {
        console.warn('Too many requests too quickly, please wait.');
        return;
    }

    isAddingToCart = true;
    lastAddToCartTime = now;
    const form = button.closest('form');
    let formData;
    let productId = '';
    let productTitle = '';
    let productImage = '';

    // Get cart add URL from form, button, or fallback to settings/default
    let cartAddUrl = form?.dataset.cartAddUrl ||
        button?.dataset.cartAddUrl ||
        (/** @type {any} */ (window).cartModalSettings?.cartAddUrl) ||
        '/cart/add.js';

    // Ensure URL ends with .js for JSON responses
    if (!cartAddUrl.endsWith('.js')) {
        cartAddUrl = cartAddUrl + '.js';
    }

    if (form) {
        formData = new FormData(form);

        // Ensure quantity is properly set (handle select elements with "10+" values)
        const quantityField = form.querySelector('[name="quantity"]');
        if (quantityField) {
            const qtyElement = /** @type {HTMLInputElement | HTMLSelectElement} */ (quantityField);
            let quantityValue = qtyElement.value;
            // If value ends with "+", extract the number part
            if (quantityValue && quantityValue.toString().endsWith('+')) {
                quantityValue = quantityValue.toString().replace('+', '');
            }
            // Ensure quantity is at least 1
            const quantity = parseInt(quantityValue, 10) || 1;
            formData.set('quantity', quantity.toString());
        } else {
            formData.set('quantity', '1');
        }

        productId = form.dataset.productId || (form.querySelector('[name="id"]') ? (/** @type {HTMLInputElement} */ (form.querySelector('[name="id"]')).value) : '');
        productTitle = form.dataset.productTitle || '';
        productImage = form.dataset.productImage || '';
    } else {
        // Fallback for buttons not in a form (like sticky bar or cards)
        productId = button.dataset.productId || '';
        productTitle = button.dataset.productTitle || '';
        productImage = button.dataset.productImage || '';

        // Find variant ID in descendants or nearest sibling container
        const variantInput = button.querySelector('input[name="id"]') ||
            button.parentElement?.querySelector('input[name="id"]');
        const variantId = variantInput ? (/** @type {HTMLInputElement} */ (variantInput)).value : '';

        // Find quantity in nearest container (check both input and select)
        const container = button.closest('div') || button.parentElement;
        const qtyInput = container?.querySelector('input[name="quantity"], select[name="quantity"], [data-quantity-input], [data-quantity-select-collection]');
        let quantity = '1';
        if (qtyInput) {
            const qtyElement = /** @type {HTMLInputElement | HTMLSelectElement} */ (qtyInput);
            quantity = qtyElement.value || '1';
            // If value ends with "+", extract the number part
            if (quantity.toString().endsWith('+')) {
                quantity = quantity.toString().replace('+', '');
            }
            // Ensure quantity is at least 1
            const qtyNum = parseInt(quantity, 10) || 1;
            quantity = qtyNum.toString();
        }

        formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
    }

    // Disable button during request
    const submitButton = /** @type {HTMLButtonElement} */ (button);
    submitButton.disabled = true;
    const originalText = submitButton.innerHTML;

    submitButton.innerHTML = '...';

    try {
        const response = await fetch(cartAddUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/json',
            },
            credentials: 'same-origin',
        });

        // Read response as text first to handle both JSON and HTML responses
        const responseText = await response.text();

        if (!response.ok) {
            // Handle 429 (Too Many Requests) specifically - often returns HTML in dev
            if (response.status === 429) {
                console.warn('429 Too Many Requests - rate limited');
                const retryAfter = response.headers.get('Retry-After');
                const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 2000;

                // Try to extract message from HTML if it's not JSON
                let errorMessage = 'Te veel verzoeken. Wacht even en probeer het opnieuw.';
                if (responseText) {
                    try {
                        const errorData = JSON.parse(responseText);
                        errorMessage = errorData.message || errorData.description || errorMessage;
                    } catch (e) {
                        // HTML response - extract text from common error page patterns
                        const htmlMatch = responseText.match(/<title[^>]*>([^<]+)<\/title>/i) ||
                            responseText.match(/<h1[^>]*>([^<]+)<\/h1>/i) ||
                            responseText.match(/<p[^>]*>([^<]+)<\/p>/i);
                        if (htmlMatch && htmlMatch[1]) {
                            errorMessage = htmlMatch[1].trim();
                        }
                    }
                }

                alert(errorMessage);

                // Reset flag after delay to allow retry
                setTimeout(() => {
                    isAddingToCart = false;
                }, waitTime);

                submitButton.disabled = false;
                submitButton.innerHTML = originalText;
                return;
            }

            // Try to parse error message from response (JSON or HTML)
            let errorMessage = `HTTP error! status: ${response.status}`;
            if (responseText) {
                try {
                    const errorData = JSON.parse(responseText);
                    errorMessage = errorData.message || errorData.description || errorMessage;
                } catch (e) {
                    // Not JSON - might be HTML error page
                    // Try to extract meaningful text from HTML
                    const htmlMatch = responseText.match(/<title[^>]*>([^<]+)<\/title>/i) ||
                        responseText.match(/<h1[^>]*>([^<]+)<\/h1>/i) ||
                        responseText.match(/<p[^>]*>([^<]+)<\/p>/i);
                    if (htmlMatch && htmlMatch[1]) {
                        errorMessage = htmlMatch[1].trim();
                    } else if (responseText.length < 200) {
                        // Use raw text if short enough
                        errorMessage = responseText.trim();
                    }
                }
            }

            // Handle 401 specifically
            if (response.status === 401) {
                console.error('401 Unauthorized - authentication issue');
                alert('Authenticatiefout. Probeer de pagina te verversen en opnieuw te proberen.');
            } else {
                alert(errorMessage);
            }

            isAddingToCart = false;
            submitButton.disabled = false;
            submitButton.innerHTML = originalText;
            return;
        }

        // Parse successful response as JSON
        let data;
        try {
            data = JSON.parse(responseText);
        } catch (e) {
            // If response is HTML (shouldn't happen on success, but handle gracefully)
            if (responseText.trim().startsWith('<')) {
                console.error('Received HTML instead of JSON on success response:', responseText.substring(0, 200));
                alert('Onverwachte server response. Probeer het opnieuw.');
            } else {
                console.error('Error parsing JSON response:', e, responseText.substring(0, 200));
                alert('Er is een fout opgetreden bij het toevoegen aan winkelmandje.');
            }
            isAddingToCart = false;
            submitButton.disabled = false;
            submitButton.innerHTML = originalText;
            return;
        }

        if (data.status === 422 || data.errors) {
            const errorMsg = data.message || data.description || 'Niet genoeg voorraad';

            // Check if it's a sold out error
            if (errorMsg.toLowerCase().includes('uitverkocht') || errorMsg.toLowerCase().includes('sold out')) {
                submitButton.disabled = true;
                submitButton.classList.add('opacity-50', 'cursor-not-allowed');
                submitButton.innerHTML = 'Uitverkocht';
                alert('Dit product is momenteel niet op voorraad.');
            } else {
                alert(errorMsg);
                submitButton.disabled = false;
                submitButton.innerHTML = originalText;
            }
            isAddingToCart = false;
            return;
        }

        // Refresh cart count and update icons
        await updateCartState(productId);

        // Show modal
        showCartModal(productTitle, productImage, productId);

        // Reset button and allow next add-to-cart
        submitButton.disabled = false;
        submitButton.innerHTML = originalText;
        isAddingToCart = false;
    } catch (error) {
        console.error('Error adding to cart:', error);
        alert('Er is een fout opgetreden bij het toevoegen aan winkelmandje.');
        submitButton.disabled = false;
        submitButton.innerHTML = originalText;
        isAddingToCart = false;
    }
}

/**
 * @param {string} productId
 */
async function updateCartState(productId) {
    try {
        const cartJsUrl = (/** @type {any} */ (window).cartModalSettings?.cartJsUrl || '/cart.js');
        const response = await fetch(cartJsUrl);
        if (!response.ok) return;

        const cartData = await response.json();
        const itemCount = cartData.item_count || 0;

        // Dispatch global event
        const cartUpdateEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
                resource: cartData,
                sourceId: 'cart-confirmation',
                data: {
                    itemCount: itemCount,
                    productId: productId,
                },
            },
        });
        document.dispatchEvent(cartUpdateEvent);

        // Update cart icons
        const cartIcons = document.querySelectorAll('cart-icon');
        cartIcons.forEach((el) => {
            const cartIcon = /** @type {any} */ (el);
            if (typeof cartIcon.renderCartBubble === 'function') {
                cartIcon.renderCartBubble(itemCount, false, true);
            } else {
                const countEl = cartIcon.querySelector('.cart-count, [ref="cartBubbleCount"]');
                if (countEl) countEl.textContent = String(itemCount);
            }
        });

        // Update sessionStorage
        sessionStorage.setItem('cart-count', JSON.stringify({
            value: String(itemCount),
            timestamp: Date.now()
        }));
    } catch (e) {
        console.error('Error updating cart state:', e);
    }
}

/**
 * @param {string} title
 * @param {string} image
 * @param {string} productId
 */
function showCartModal(title, image, productId) {
    const modal = document.getElementById('cart-confirmation-modal');
    const productImageEl = /** @type {HTMLImageElement | null} */ (document.getElementById('added-product-image'));
    const productTitleEl = document.getElementById('added-product-title');

    if (!modal) return;

    if (productImageEl && image) productImageEl.src = image;
    if (productTitleEl && title) productTitleEl.textContent = title;

    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';

    // Load recommendations
    if (productId) {
        loadModalRecommendations(productId);
    }
}

function closeCartModal() {
    const modal = document.getElementById('cart-confirmation-modal');
    if (modal) {
        modal.classList.add('hidden');
        document.body.style.overflow = '';
    }
}

/**
 * @param {string} productId
 */
async function loadModalRecommendations(productId) {
    const container = document.getElementById('recommended-products');
    if (!container) return;

    container.innerHTML = '<li class="col-span-4 text-center py-8"><div class="animate-pulse text-gray-400">Laden...</div></li>';

    try {
        const baseRecUrl = (/** @type {any} */ (window).cartModalSettings?.recommendationsUrl || '/recommendations/products');
        const recUrl = `${baseRecUrl}.json?product_id=${productId}&limit=4`;

        const response = await fetch(recUrl);

        if (response.ok) {
            const data = await response.json();
            const products = data.products || [];

            if (products.length > 0) {
                renderModalProducts(products, container);
                return;
            }
        }

        // Fallback to products-data.json
        await loadFallbackModalRecommendations(productId, container);
    } catch (error) {
        console.error('Error loading recommendations:', error);
        await loadFallbackModalRecommendations(productId, container);
    }
}

/**
 * @param {string} productId
 * @param {HTMLElement} container
 */
async function loadFallbackModalRecommendations(productId, container) {
    try {
        const productsDataUrl = (/** @type {any} */ (window).cartModalSettings?.productsDataUrl);
        if (!productsDataUrl) return;

        const response = await fetch(productsDataUrl);
        if (!response.ok) return;

        const data = await response.json();
        const rawProducts = Array.isArray(data.products) ? data.products : [];

        // Transform Shopify JSON format to expected format
        const allProducts = rawProducts.map(product => {
            const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
            const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;

            return {
                id: product.id,
                handle: product.handle,
                title: product.title,
                product_type: product.product_type || '',
                price: firstVariant ? parseFloat(firstVariant.price) : 0,
                price_cents: firstVariant ? Math.round(parseFloat(firstVariant.price) * 100) : 0,
                compare_at_price: firstVariant && firstVariant.compare_at_price ? parseFloat(firstVariant.compare_at_price) : null,
                compare_at_price_cents: firstVariant && firstVariant.compare_at_price ? Math.round(parseFloat(firstVariant.compare_at_price) * 100) : 0,
                variant_id: firstVariant ? firstVariant.id : null,
                available: firstVariant ? firstVariant.available : false,
                featured_image: firstImage ? firstImage.src : '',
                url: `/products/${product.handle}`,
                description: product.body_html ? product.body_html.replace(/<[^>]*>/g, '').substring(0, 150) : '',
                metafields: {} // Shopify public API doesn't include metafields
            };
        });

        // Find random products excluding current
        const related = allProducts
            .filter((/** @type {any} */ p) => String(p.id) !== String(productId))
            .sort(() => 0.5 - Math.random())
            .slice(0, 4);

        if (related.length > 0) {
            renderModalProducts(related, container);
        } else {
            container.innerHTML = '<li class="col-span-4 text-center py-8 text-gray-500 text-sm">Geen aanbevelingen gevonden.</li>';
        }
    } catch (e) {
        console.error('Fallback error:', e);
    }
}

/**
 * Get variant ID for cart add. Recommendations API uses product.variants[0].id (never product.id).
 * @param {any} product
 * @returns {string|number}
 */
function getRecommendationVariantId(product) {
    if (product.variants && product.variants.length > 0) {
        const v = product.variants[0];
        return (v && typeof v === 'object' && 'id' in v) ? v.id : v;
    }
    if (product.first_available_variant && product.first_available_variant.id) return product.first_available_variant.id;
    if (product.variant_id != null) return product.variant_id;
    return '';
}

/**
 * @param {any[]} products
 * @param {HTMLElement} container
 */
function renderModalProducts(products, container) {
    container.innerHTML = '';

    const cartAddUrl = (/** @type {any} */ (window).cartModalSettings?.cartAddUrl) || '/cart/add.js';

    products.forEach((product) => {
        const variantId = getRecommendationVariantId(product);
        if (!variantId) return; // skip products with no variant (can't add to cart)

        const imageObj = product.featured_image || product.featured_media?.preview_image || product.images?.[0];
        const imageUrl = typeof imageObj === 'string' ? imageObj : (imageObj?.src || imageObj?.url || '');
        const rawPrice = product.price ?? product.price_min ?? product.price_cents ?? 0;
        const priceCents = Number(rawPrice);
        const price = (priceCents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
        const productUrl = product.url || (product.handle ? `/products/${product.handle}` : '#');
        const title = (product.title || '').replace(/</g, '&lt;').replace(/"/g, '&quot;');

        const li = document.createElement('li');
        li.className = 'group bg-white rounded-bl-lg rounded-tr-lg overflow-hidden border border-gray-100 p-2 lg:p-3';
        li.innerHTML = `
            <div class="aspect-square bg-[#eef8f3] rounded-lg mb-3 relative overflow-hidden">
                <a href="${productUrl}" class="block h-full w-full p-4">
                    <img src="${imageUrl}" alt="${title}" class="w-full h-full object-contain">
                </a>
            </div>
            <a href="${productUrl}" class="block mb-2">
                <h4 class="text-xs lg:text-sm font-bold text-[#006837] line-clamp-2 h-8 lg:h-10">${title}</h4>
            </a>
            <div class="flex items-center justify-between gap-2 mt-auto">
                <span class="text-sm lg:text-base font-bold text-orange-500">${price}</span>
                <form class="add-to-cart-form modal-recommendation-form" data-product-id="${product.id}" data-product-title="${(product.title || '').replace(/"/g, '&quot;')}" data-product-image="${imageUrl}" data-product-url="${productUrl}" data-cart-add-url="${cartAddUrl}">
                    <input type="hidden" name="id" value="${variantId}">
                    <input type="hidden" name="quantity" value="1">
                    <button type="button" class="modal-recommendation-add-btn bg-[#22b573] hover:bg-[#006837] text-white w-9 h-9 rounded-bl-lg rounded-tr-lg transition-colors flex items-center justify-center" aria-label="In winkelmandje">
                        <svg class="w-5 h-5 max-w-5 max-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                        </svg>
                    </button>
                </form>
            </div>
        `;
        container.appendChild(li);
    });
}

// Event delegation: handle add-to-cart for modal recommendation buttons (works for dynamically injected content)
function initModalRecommendationDelegation() {
    const modal = document.getElementById('cart-confirmation-modal');
    if (!modal) return;
    modal.addEventListener('click', function (e) {
        const btn = e.target && (/** @type {HTMLElement} */ (e.target)).closest('.modal-recommendation-add-btn');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        const form = btn.closest('form.modal-recommendation-form');
        if (form && typeof addToCartWithModal === 'function') {
            addToCartWithModal(/** @type {HTMLElement} */(btn));
        }
    });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModalRecommendationDelegation);
} else {
    initModalRecommendationDelegation();
}

// Global exposure
(/** @type {any} */ (window)).addToCartWithModal = addToCartWithModal;
(/** @type {any} */ (window)).closeCartModal = closeCartModal;
(/** @type {any} */ (window)).showCartModal = showCartModal;
